import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUsersAsync } from '../../../features/users/usersActions';
import { selectAllUsers, selectCanManageUsers } from '../../../features/users/usersSlice';
import { fetchDepartments } from '../../../features/departments/departmentActions';
import { fetchJobTitles } from '../../../features/jobTitles/jobTitleActions';
const UsersContext = createContext(null);
export function UsersProvider({ children }) {
  const dispatch = useDispatch();
  const users = useSelector(selectAllUsers);
  const registry = useSelector(state => state.users.registry);
  const canManage = useSelector(selectCanManageUsers);
  const departmentRecords = useSelector(state => state.departments.items);
  const jobTitleRecords = useSelector(state => state.jobTitles.items);
  const optionsError = useSelector(state => state.departments.error || state.jobTitles.error);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [branch, setBranch] = useState('');
  const [department, setDepartment] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [sortAscending, setSortAscending] = useState(true);
  const [page, setPage] = useState(1);
  const reload = useCallback(() => dispatch(getUsersAsync()), [dispatch]);
  useEffect(() => { const request = reload(); return () => request.abort(); }, [reload]);
  useEffect(() => { const requests = [dispatch(fetchDepartments()), dispatch(fetchJobTitles())]; return () => requests.forEach(request => request.abort()); }, [dispatch]);
  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter(user => (!status || user.status === status) && (!role || user.role?.name === role) &&
      (!branch || user.branchName === branch) && (!department || String(user.departmentId || 'legacy:' + user.departmentName) === department) &&
      (!jobTitle || String(user.jobTitleId || 'legacy:' + user.jobTitle) === jobTitle) &&
      [user.fullName, user.employeeId, user.email, user.mobileNumber, user.role?.name].some(value => String(value || '').toLowerCase().includes(query)))
      .sort((a, b) => a.fullName.localeCompare(b.fullName) * (sortAscending ? 1 : -1));
  }, [users, search, status, role, branch, department, jobTitle, sortAscending]);
  const stats = useMemo(() => ({
    total: users.length,
    admins: users.filter(user => user.role?.name === 'ADMIN').length,
    managers: users.filter(user => user.role?.name === 'MANAGER').length,
    staff: users.filter(user => user.role?.name === 'STAFF').length,
    inactive: users.filter(user => user.status === 'INACTIVE').length,
  }), [users]);
  const pageSize = 6;
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const updateSearch = value => { setSearch(value); setPage(1); };
  const updateStatus = value => { setStatus(value); setPage(1); };
  const filterSetter = setter => value => { setter(value); setPage(1); };
  const clearFilters = () => { setSearch(''); setStatus(''); setRole(''); setBranch(''); setDepartment(''); setJobTitle(''); setPage(1); };
  const options = field => [...new Set(users.map(user => field === 'role' ? user.role?.name : user[field]).filter(Boolean))].sort();
  const departmentOptions = departmentRecords.map(item => ({ value: String(item.id), label: item.name + (!item.isActive ? ' (Inactive)' : '') }));
  const jobTitleOptions = jobTitleRecords.map(item => ({ value: String(item.id), label: item.name + ' · ' + item.department.name + (!item.isActive ? ' (Inactive)' : '') }));
  for (const user of users) {
    if (!user.departmentId && user.departmentName && !departmentOptions.some(item => item.value === 'legacy:' + user.departmentName)) departmentOptions.push({ value: 'legacy:' + user.departmentName, label: user.departmentName + ' (Unlinked)' });
    if (!user.jobTitleId && user.jobTitle && !jobTitleOptions.some(item => item.value === 'legacy:' + user.jobTitle)) jobTitleOptions.push({ value: 'legacy:' + user.jobTitle, label: user.jobTitle + ' (Unlinked)' });
  }
  return <UsersContext.Provider value={{ users, stats, canManage, search, setSearch: updateSearch,
    role, setRole: filterSetter(setRole), branch, setBranch: filterSetter(setBranch),
    department, setDepartment: filterSetter(setDepartment), jobTitle, setJobTitle: filterSetter(setJobTitle),
    roles: options('role'), branches: options('branchName'), departments: departmentOptions, jobTitles: jobTitleOptions,
    optionsError, reloadOptions: () => { dispatch(fetchDepartments()); dispatch(fetchJobTitles()); },
    clearFilters, hasFilters: Boolean(search || status || role || branch || department || jobTitle),
    sortAscending, toggleSort: () => { setSortAscending(value => !value); setPage(1); }, pageSize,
    status, setStatus: updateStatus, page: currentPage, setPage, totalPages,
    filteredCount: filteredUsers.length, visibleUsers: filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    loading: registry.loading || !registry.requestId, error: registry.error, reload }}>{children}</UsersContext.Provider>;
}
export function useUsers() {
  const context = useContext(UsersContext);
  if (!context) throw new Error('useUsers requires UsersProvider.');
  return context;
}
